import { ModelTreeNode } from "@/types/ModelTreeNode";

import { CadModelCategory, Category, Co2MaterialType, Element, FeatureManagementType, ExtendedDataKey, Material, ModelExtendedDataDto, Supplemental, SupplementalType } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useCo2MaterialTypes } from "@api/co2MaterialTypes";
import { useCategories, useComponents, useElements, useMaterials, useModelExtendedData } from "@api/models";
import useAppStore from "@hooks/useAppStore";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { visitNodes } from "@utils/treeNodes";
import i18n from "@i18n/i18n";
import { MutableRefObject, useEffect, useMemo, useState } from "react";

export interface UseScenarioModelProps {
  apiRef: MutableRefObject<GridApiPremium>;
  modelId: string | undefined;
}

const useScenarioModel = (props: UseScenarioModelProps) => {
  const { modelId, apiRef } = props;
  const [hasCo2MaterialTypeSpecs, setHasCo2MaterialTypeSpecs] = useState(false);
  const [co2MaterialTypeSpecKind, setCo2MaterialTypeSpecKind] = useState<string | null>(null);
  const appStore = useAppStore();
  const co2MaterialTypes = useCo2MaterialTypes();
  const cadModelCategories = useCadModelCategories();
  const categories = useCategories(props.modelId, appStore.scenario?.scenarioId);
  const elements = useElements(props.modelId, appStore.scenario?.scenarioId, {
    enabled: !!props.modelId && !!appStore.scenario?.scenarioId,
  });
  const components = useComponents(props.modelId, appStore.scenario?.scenarioId, {
    enabled: !!props.modelId && !!appStore.scenario?.scenarioId,
  });
  const materials = useMaterials(props.modelId, appStore.scenario?.scenarioId, {
    enabled: !!elements.data && !!components.data,
  });

  const enableRebarLineItems =
    appStore.applicationSettings?.features?.includes(
      FeatureManagementType.EnableRebarLineItems
    );

  const enableNonModelledLineItems =
    appStore.applicationSettings?.features?.includes(
      FeatureManagementType.EnableNonModelledLineItems
    );

  const modelExtendedData = useModelExtendedData(props.modelId, appStore.scenario?.scenarioId, { enabled: !!elements.data && !!components.data });
  useEffect(() => {
  }, [modelExtendedData.isLoading, modelExtendedData.error, modelExtendedData.data]);

  const loading = useMemo(() => {
    return cadModelCategories.isFetching || categories.isFetching || elements.isFetching || components.isFetching || materials.isFetching || modelExtendedData.isFetching;
  }, [cadModelCategories.isFetching, categories.isFetching, elements.isFetching, components.isFetching, materials.isFetching, modelExtendedData.isFetching]);

  useEffect(() => {
  }, [co2MaterialTypes.data]);

  useEffect(() => {
  }, [cadModelCategories.data]);

  useEffect(() => {
    //const elementsWithSupplementals = elements.data.filter((e: Element) => (e.supplementals?.length || 0) > 0);
    //console.log("elementsWithSupplementals: ", elementsWithSupplementals);
  }, [elements.data]);

  useEffect(() => {
    //const componentsWithSupplementals = components.data.filter((c: Component) => (c.supplementals?.length || 0) > 0);
    //console.log("componentsWithSupplementals: ", componentsWithSupplementals);
  }, [components.data]);

  useEffect(() => {

    if (!elements.data || !components.data || !materials.data || !modelExtendedData.data || !co2MaterialTypes.data || !cadModelCategories.data) {
      return;
    }

    // check if modelExtendedData has any Co2MaterialType values
    const hasCo2MaterialTypeSpec = (modelExtendedData && Object.values(modelExtendedData.data ?? {}).some((data) => data?.hasOwnProperty(ExtendedDataKey.Co2MaterialTypeSpec))) || false;
    setHasCo2MaterialTypeSpecs(hasCo2MaterialTypeSpec);

    // Extract Kind property from Co2MaterialTypeSpec using null coalescing
    const extractedKind = Object.values(modelExtendedData.data ?? {})
      .find(data => data?.[ExtendedDataKey.Co2MaterialTypeSpec])
      ?.[ExtendedDataKey.Co2MaterialTypeSpec]?.Kind ?? null;
    setCo2MaterialTypeSpecKind(extractedKind);

    const materialsLookup = materials.data?.reduce((acc: any, m: Material) => {
      if (m.parentId) {
        acc.set(m.parentId, m);
      }
      return acc;
    }, new Map<string, Material>());

    const co2MaterialTypesLookup = co2MaterialTypes.data?.reduce(
      (acc: any, m: Co2MaterialType) => {
        if (m.co2MaterialTypeId) {
          acc.set(m.co2MaterialTypeId, m);
        }
        return acc;
      },
      new Map<string, Co2MaterialType>()
    );

    const cadModelCategoriesLookup = cadModelCategories.data?.reduce(
      (acc: any, c: CadModelCategory) => {
        if (c.cadModelCategoryId) {
          acc.set(c.cadModelCategoryId, c);
        }
        return acc;
    }, new Map<string, CadModelCategory>());

    updateElementData(elements.data || [], materialsLookup, co2MaterialTypesLookup, cadModelCategoriesLookup, hasCo2MaterialTypeSpecs, modelExtendedData.data);
    updateComponentData(components.data || [], materialsLookup, co2MaterialTypesLookup, cadModelCategoriesLookup, hasCo2MaterialTypeSpecs, modelExtendedData.data);

    updateAssignNodes();
  }, [elements.data, components.data, materials.data, modelExtendedData.data, co2MaterialTypes.data, cadModelCategories.data]);

  //const enablePrefix = toBoolean(searchParams.get("prefix") ?? false);
  const enablePrefix = false;
  const caPrefix = enablePrefix ? "ca:" : "";
  const gPrefix = enablePrefix ? "g:" : "";
  const ePrefix = enablePrefix ? "e:" : "";
  const coPrefix = enablePrefix ? "co:" : "";

  const updateElementData = (
    elements: Element[],
    materials: Map<string, Material> | null,
    co2MaterialTypes: Map<string, Co2MaterialType> | null,
    cadModelCategories: Map<string, CadModelCategory> | null,
    hasCo2MaterialTypes: boolean,
    modelExtendedData?: ModelExtendedDataDto
  ) => {
    if ((elements?.length || 0) > 0) {
      elements.forEach((e: Element) => {
        const material = materials?.get(e.elementId!);
        if (material) {
          material.co2MaterialType = co2MaterialTypes?.get(material.co2MaterialTypeId!);
          (e as any).material = material;
        }

        const cadModelCategory = cadModelCategories?.get(e.cadModelCategoryId!);
        if (cadModelCategory) {
          (e as any).cadModelCategory = cadModelCategory;
        }

        // Check supplementals
        let hasRebarWithValue = false;
        let hasNonModelledWithValue = false;

        if (enableRebarLineItems && (e.supplementals?.length || 0) > 0) {
          hasRebarWithValue = (e.supplementals || []).some(
            (s: Supplemental) => s.supplementalType === SupplementalType.Rebar && (s.value ?? 0) > 0
          );
        }

        if (enableNonModelledLineItems && (e.supplementals?.length || 0) > 0) {
          hasNonModelledWithValue = (e.supplementals || []).some(
            (s: Supplemental) => s.supplementalType === SupplementalType.NonModelled && (s.value ?? 0) > 0
          );
        }

        if (hasRebarWithValue || hasNonModelledWithValue) {
          const primary = e.supplementals?.find(
            (s: Supplemental) => s.supplementalType === SupplementalType.Primary
          );
          if (primary) {
            // Override with Primary Supplemental values (only when rebar or non-modelled has value > 0).
            e.volume = primary.volume ?? e.volume;
            e.volumeTotal = primary.volume ?? e.volumeTotal;
            e.co2Totals = primary.co2Totals ?? e.co2Totals;
          }
        }

        if (hasCo2MaterialTypes && modelExtendedData) {
          const extendedData = modelExtendedData[e.elementId!];
          if (extendedData) {
            e.co2MaterialTypeSpec = extendedData[ExtendedDataKey.Co2MaterialTypeSpec];
          }
        }

        e.supplementals?.forEach((s: Supplemental) => {
          const sm = materials?.get(s.supplementalId!);
          if (sm) {
            sm.co2MaterialType = co2MaterialTypes?.get(sm.co2MaterialTypeId!);
            (s as any).material = sm;
          }
        });
      });

      appStore.queryClient.setQueryData([["elements", modelId]], () => {
        return { data: elements };
      });
    }
  };

  const updateComponentData = (
    components: Element[],
    materials: Map<string, Material> | null,
    co2MaterialTypes: Map<string, Co2MaterialType> | null,
    cadModelCategories: Map<string, CadModelCategory> | null,
    hasCo2MaterialTypes: boolean,
    modelExtendedData?: ModelExtendedDataDto
  ) => {
    // TODO: could turn this into a lookup table instead
    if ((components?.length || 0) > 0) {
      components.forEach((c: Element) => {
        const material = materials?.get(c.elementId!);
        if (material) {
          material.co2MaterialType = co2MaterialTypes?.get(material.co2MaterialTypeId!);
          (c as any).material = material;
        }

        const cadModelCategory = cadModelCategories?.get(c.cadModelCategoryId!);
        if (cadModelCategory) {
          (c as any).cadModelCategory = cadModelCategory;
        }

        // Check supplementals
        let hasRebarWithValue = false;
        let hasNonModelledWithValue = false;

        if (enableRebarLineItems && (c.supplementals?.length || 0) > 0) {
          hasRebarWithValue = (c.supplementals || []).some(
            (s: Supplemental) => s.supplementalType === SupplementalType.Rebar && (s.value ?? 0) > 0
          );
        }

        if (enableNonModelledLineItems && (c.supplementals?.length || 0) > 0) {
          hasNonModelledWithValue = (c.supplementals || []).some(
            (s: Supplemental) => s.supplementalType === SupplementalType.NonModelled && (s.value ?? 0) > 0
          );
        }

        if (hasRebarWithValue || hasNonModelledWithValue) {
          const primary = c.supplementals?.find(
            (s: Supplemental) => s.supplementalType === SupplementalType.Primary
          );
          if (primary) {
            // Override with Primary Supplemental values (only when rebar or non-modelled has value > 0).
            c.volume = primary.volume ?? c.volume;
            c.volumeTotal = primary.volume ?? c.volumeTotal;
            c.co2Totals = primary.co2Totals ?? c.co2Totals;
          }
        }

        if (hasCo2MaterialTypes && modelExtendedData) {
          const extendedData = modelExtendedData[c.elementId!];
          if (extendedData) {
            c.co2MaterialTypeSpec = extendedData[ExtendedDataKey.Co2MaterialTypeSpec];
          }
        }

        c.supplementals?.forEach((s: Supplemental) => {
          const sm = materials?.get(s.supplementalId!);
          if (sm) {
            sm.co2MaterialType = co2MaterialTypes?.get(sm.co2MaterialTypeId!);
            (s as any).material = sm;
          }
        });
      });

      appStore.queryClient.setQueryData([["components", modelId]], () => {
        return { data: components };
      });
    }
  };

  const makeRebarNode = (e: Element, s: Supplemental): ModelTreeNode => {
    const label = `${i18n.t("assign-grid.rebar.prefix")}_${e.cadName || ""}`;
    return {
      type: "supplemental",
      key: s.supplementalId,
      data: {
        ...s,
        parentElementId: e.elementId,
        cadFamily: (e as any).cadFamily ?? "",
        cadName: label,
        label,
        assigned: (e as any).assigned,
        volumeTotal: s.volume ?? 0,
        quantityTotal: 1,
        siblings: 1,
      },
      leaf: true,
    };
  };

  const makeNonModelledNode = (e: Element, s: Supplemental): ModelTreeNode => {
    const label = `${i18n.t("assign-grid.nonmodelled.prefix")}_${e.cadName || ""}`;
    return {
      type: "supplemental",
      key: s.supplementalId,
      data: {
        ...s,
        parentElementId: e.elementId,
        cadFamily: (e as any).cadFamily ?? "",
        cadName: label,
        label,
        assigned: (e as any).assigned,
        volumeTotal: s.volume ?? 0,
        quantityTotal: 1,
        siblings: 1,
      },
      leaf: true,
    };
  };

  const updateAssignNodes = () => {
    // Group by categories, then families, then types
    let treeNodes: ModelTreeNode[] =
      (categories.data?.map((c: Category) => {
        //  Build category children separately for elements and supplementals
        const elementNodes: ModelTreeNode[] = [];
        const supplementalNodes: ModelTreeNode[] = [];

        // Map to keep elementId -> rebar nodes (we'll inject into family children later)
        const elementRebarMap = new Map<string, ModelTreeNode[]>();
        const elementNonModelledMap = new Map<string, ModelTreeNode[]>();

        elements.data
          ?.filter((e: Element) => c.cadModelCategoryId === e.cadModelCategoryId)
          .forEach((e: Element) => {
            (e as any).siblings = e.childCount || 1;
            (e as any).label = ePrefix + (e.cadName || "");

            // Normal element node
            elementNodes.push({
              type: "element",
              key: e.elementId,
              data: e,
              leaf: e.childCount === 0,
            });

            // Collect element-level rebar nodes into map (do NOT push into elementNodes now)
            if (enableRebarLineItems && (e.supplementals?.length || 0) > 0) {
              const rebarForElement: ModelTreeNode[] = [];
              e.supplementals
                ?.filter((s) => s.supplementalType === SupplementalType.Rebar && (s.value ?? 0) > 0)
                .forEach((s) => {
                  const rebarNode = makeRebarNode(e, s);
                  rebarForElement.push(rebarNode);
                  // also keep supplementalNodes as fallback when no elements exist
                  supplementalNodes.push(rebarNode);
                });

              if (rebarForElement.length > 0) {
                elementRebarMap.set(e.elementId!, rebarForElement);
              }
            }

            // Collect element-level non-modelled nodes into map (do NOT push into elementNodes now)
            if (enableNonModelledLineItems && (e.supplementals?.length || 0) > 0) {
              const nonModelledForElement: ModelTreeNode[] = [];
              e.supplementals
                ?.filter((s) => s.supplementalType === SupplementalType.NonModelled && (s.value ?? 0) > 0)
                .forEach((s) => {
                  const nonModelledNode = makeNonModelledNode(e, s);
                  nonModelledForElement.push(nonModelledNode);
                  // also keep supplementalNodes as fallback when no elements exist
                  supplementalNodes.push(nonModelledNode);
                });

              if (nonModelledForElement.length > 0) {
                elementNonModelledMap.set(e.elementId!, nonModelledForElement);
              }
            }
          });


        // Apply groupings
        let families: any = null;

        if (elementNodes.length > 0) {
          // group ONLY elements (supplementals are injected later into family children)
          const familyGroups = groupBy(elementNodes, (n: any) => n.data?.cadFamily || "");
          families = processGroupNodes(familyGroups, c.cadModelCategoryId);

          // For each family, regroup by element cadName and then inject any supplemental that belongs to an element in this family
          families.forEach((family: any, familyIndex: number) => {
            const elementGroup = groupBy(family.children, (n: any) => {
              const prefix = n.data.cadFamily === n.data.cadName ? `${c.cadModelCategoryId}/` : "";
              return prefix + (n.data.cadName || "").replace("/", "");
            });
            family.children = processGroupNodes(elementGroup, `${c.cadModelCategoryId}/${familyIndex.toString()}`, 1);
            family.data.assigned = family.children.reduce((acc: boolean, e: any) => acc && e.data.assigned, true);

            // INJECT rebar nodes next to their element inside family.children
            if (enableRebarLineItems && elementRebarMap.size > 0) {
              // Build new children array with rebar nodes injected immediately after their parent element node.
              // Match by data.elementId (because processGroupNodes may change node.key).
              const newChildren: any[] = [];
              family.children.forEach((child: any) => {
                // If child is a group itself (unlikely here), flatten small groups to find element nodes inside.
                // But in the normal case child.type === 'element' or child.type === 'group' representing grouped elements.
                if (child.type === "group" && Array.isArray(child.children)) {
                  // preserve outer group, but inject rebar nodes inside its children as needed
                  const innerChildren: ModelTreeNode[] = [];
                  child.children.forEach((inner: any) => {
                    innerChildren.push(inner);
                    const elementId = inner?.data?.elementId;
                    if (elementId && elementRebarMap.has(elementId)) {
                      const rebarNodesForElement = elementRebarMap.get(elementId) || [];
                      const existingKeys = new Set(innerChildren.map((ch) => ch.key));
                      rebarNodesForElement.forEach((rn) => {
                        if (!existingKeys.has(rn.key)) {
                          innerChildren.push(rn);
                        }
                      });
                    }
                  });
                  // replace group's children with injected innerChildren
                  const newGroup = { ...child, children: innerChildren };
                  newChildren.push(newGroup);
                } else {
                  newChildren.push(child);

                  // If the child is an element and there are rebar nodes for it, inject them
                  const elementId = child?.data?.elementId;
                  if (elementId && elementRebarMap.has(elementId)) {
                    const rebarNodesForElement = elementRebarMap.get(elementId) || [];
                    // Ensure no duplicates: check keys not already present
                    const existingKeys = new Set(newChildren.map((ch) => ch.key));
                    rebarNodesForElement.forEach((rn) => {
                      if (!existingKeys.has(rn.key)) {
                        newChildren.push(rn);
                      }
                    });
                  }
                }
              });

              family.children = newChildren;

              // Update family totals to include any injected rebar nodes
              const injectedRebarTotals = family.children.reduce(
                (acc: { rebarVolume: number; rebarCo2: number }, child: any) => {
                  if (child.type === "supplemental" && child.data?.parentElementId && child.data?.supplementalType === SupplementalType.Rebar) {
                    acc.rebarVolume += child.data?.volume || 0;
                    acc.rebarCo2 += (child.data?.co2Totals?.totalCo2) || 0;
                  } else if (child.type === "group" && Array.isArray(child.children)) {
                    // also check inside group children
                    child.children.forEach((ch: any) => {
                      if (ch.type === "supplemental" && ch.data?.parentElementId && ch.data?.supplementalType === SupplementalType.Rebar) {
                        acc.rebarVolume += ch.data?.volume || 0;
                        acc.rebarCo2 += (ch.data?.co2Totals?.totalCo2) || 0;
                      }
                    });
                  }
                  return acc;
                },
                { rebarVolume: 0, rebarCo2: 0 }
              );

              family.data.rebarVolumeTotal = (family.data.rebarVolumeTotal || 0) + injectedRebarTotals.rebarVolume;
              family.data.rebarTotalCo2 = (family.data.rebarTotalCo2 || 0) + injectedRebarTotals.rebarCo2;
            }

            // INJECT non-modelled nodes next to their element inside family.children
            if (enableNonModelledLineItems && elementNonModelledMap.size > 0) {
              // Build new children array with non-modelled nodes injected immediately after their parent element node.
              // Match by data.elementId (because processGroupNodes may change node.key).
              const newChildren: any[] = [];
              family.children.forEach((child: any) => {
                // If child is a group itself (unlikely here), flatten small groups to find element nodes inside.
                // But in the normal case child.type === 'element' or child.type === 'group' representing grouped elements.
                if (child.type === "group" && Array.isArray(child.children)) {
                  // preserve outer group, but inject non-modelled nodes inside its children as needed
                  const innerChildren: ModelTreeNode[] = [];
                  child.children.forEach((inner: any) => {
                    innerChildren.push(inner);
                    const elementId = inner?.data?.elementId;
                    if (elementId && elementNonModelledMap.has(elementId)) {
                      const nonModelledNodesForElement = elementNonModelledMap.get(elementId) || [];
                      const existingKeys = new Set(innerChildren.map((ch) => ch.key));
                      nonModelledNodesForElement.forEach((nm) => {
                        if (!existingKeys.has(nm.key)) {
                          innerChildren.push(nm);
                        }
                      });
                    }
                  });
                  // replace group's children with injected innerChildren
                  const newGroup = { ...child, children: innerChildren };
                  newChildren.push(newGroup);
                } else {
                  newChildren.push(child);

                  // If the child is an element and there are non-modelled nodes for it, inject them
                  const elementId = child?.data?.elementId;
                  if (elementId && elementNonModelledMap.has(elementId)) {
                    const nonModelledNodesForElement = elementNonModelledMap.get(elementId) || [];
                    // Ensure no duplicates: check keys not already present
                    const existingKeys = new Set(newChildren.map((ch) => ch.key));
                    nonModelledNodesForElement.forEach((nm) => {
                      if (!existingKeys.has(nm.key)) {
                        newChildren.push(nm);
                      }
                    });
                  }
                }
              });

              family.children = newChildren;

              // Update family totals to include any injected non-modelled nodes
              const injectedNonModelledTotals = family.children.reduce(
                (acc: { nonModVolume: number; nonModCo2: number }, child: any) => {
                  if (child.type === "supplemental" && child.data?.parentElementId && child.data?.supplementalType === SupplementalType.NonModelled) {
                    acc.nonModVolume += child.data?.volume || 0;
                    acc.nonModCo2 += (child.data?.co2Totals?.totalCo2) || 0;
                  } else if (child.type === "group" && Array.isArray(child.children)) {
                    // also check inside group children
                    child.children.forEach((ch: any) => {
                      if (ch.type === "supplemental" && ch.data?.parentElementId && ch.data?.supplementalType === SupplementalType.NonModelled) {
                        acc.nonModVolume += ch.data?.volume || 0;
                        acc.nonModCo2 += (ch.data?.co2Totals?.totalCo2) || 0;
                      }
                    });
                  }
                  return acc;
                },
                { nonModVolume: 0, nonModCo2: 0 }
              );

              family.data.nonModVolumeTotal = (family.data.nonModVolumeTotal || 0) + injectedNonModelledTotals.nonModVolume;
              family.data.nonModTotalCo2 = (family.data.nonModTotalCo2 || 0) + injectedNonModelledTotals.nonModCo2;
            }

            // Merge model extended data if available
            const possibleKeys = [
              family.key, // Current generated key (e.g., "categoryId/0")
              family.data.label, // Family name (e.g., "Arup_Air Terminals_Exhaust Grille...")
              `${c.cadModelCategoryId}/${family.data.label}`, // Category/Family pattern
              family.data.label?.split('_').slice(1).join('_'), // Remove first part before underscore
            ].filter(Boolean);

            let groupModelExtendedData = null;
            let matchedKey = null;

            // Try each possible key pattern
            for (const key of possibleKeys) {
              if (key && modelExtendedData.data?.[key]) {
                groupModelExtendedData = modelExtendedData.data[key];
                matchedKey = key;
                break;
              }
            }

            if (groupModelExtendedData && matchedKey) {
              family.data = {
                ...family.data,
                multiplierInput: groupModelExtendedData.MultiplierInput || family.data.multiplierInput,
                multiplierValue: groupModelExtendedData.MultiplierValue ? parseFloat(groupModelExtendedData.MultiplierValue) : family.data.multiplierValue,
                notes: groupModelExtendedData.Notes || family.data.notes,
                carbonImpactInput: groupModelExtendedData.CarbonImpactInput || family.data.CarbonImpactInput,
                carbonImpactValue: groupModelExtendedData.CarbonImpactValue ? parseFloat(groupModelExtendedData.CarbonImpactValue) : family.data.CarbonImpactValue,
                carbonImpactNotes: groupModelExtendedData.CarbonImpactNotes || family.data.CarbonImpactNotes,
                systemId: groupModelExtendedData.SystemId || family.data.systemId,
                subSystemId: groupModelExtendedData.SubSystemId || family.data.subSystemId,
                attribute1: groupModelExtendedData.Attribute1 || family.data.attribute1,
                attribute2: groupModelExtendedData.Attribute2 || family.data.attribute2,
                attribute3: groupModelExtendedData.Attribute3 || family.data.attribute3,
              };
            }
          });

          families.sort((a: any, b: any) => (a.data.label > b.data.label ? 1 : -1));
        } else {
          //  If there are no elements but there ARE supplemental rows for this category,
          // still show them (edge case)
          if ((enableRebarLineItems || enableNonModelledLineItems) && supplementalNodes.length > 0) {
            const rebarNodes = enableRebarLineItems 
              ? supplementalNodes.filter((n: any) => n.data?.supplementalType === SupplementalType.Rebar)
              : [];
            const nonModelledNodes = enableNonModelledLineItems
              ? supplementalNodes.filter((n: any) => n.data?.supplementalType === SupplementalType.NonModelled)
              : [];
            
            if (rebarNodes.length > 0 || nonModelledNodes.length > 0) {
              families = [
                {
                  type: "group",
                  key: `${gPrefix}${c.cadModelCategoryId}/supplementals`,
                  data: {
                    assigned: supplementalNodes.every((n) => n.data?.assigned),
                    lengthTotal: 0,
                    areaTotal: 0,
                    volumeTotal: 0,
                    totalCo2: supplementalNodes.reduce(
                      (acc, n: any) => acc + (n.data?.co2Totals?.totalCo2 || 0),
                      0
                    ),
                    rebarVolumeTotal: rebarNodes.reduce(
                      (acc, n: any) => acc + (n.data?.volume || 0),
                      0
                    ),
                    rebarTotalCo2: rebarNodes.reduce(
                      (acc, n: any) => acc + (n.data?.co2Totals?.totalCo2 || 0),
                      0
                    ),
                    nonModVolumeTotal: nonModelledNodes.reduce(
                      (acc, n: any) => acc + (n.data?.volume || 0),
                      0
                    ),
                    nonModTotalCo2: nonModelledNodes.reduce(
                      (acc, n: any) => acc + (n.data?.co2Totals?.totalCo2 || 0),
                      0
                    ),
                    label: `${gPrefix}Supplementals`,
                    siblings: supplementalNodes.length,
                  },
                  children: supplementalNodes,
                  leaf: supplementalNodes.length === 0,
                },
              ];
            }
          }
        }

        // Check for category-level extended data
        const categoryExtendedData = c.cadModelCategoryId && modelExtendedData.data?.[c.cadModelCategoryId];

        let categoryData: any = {
          ...c,
          label: caPrefix + cadModelCategories.data?.find((cmc) => cmc.cadModelCategoryId === c.cadModelCategoryId)?.label,
          siblings: c.childCount,
        };

        if (categoryExtendedData) {
          categoryData = {
            ...categoryData,
            multiplierInput: categoryExtendedData.MultiplierInput,
            multiplierValue: categoryExtendedData.MultiplierValue ? parseFloat(categoryExtendedData.MultiplierValue) : undefined,
            notes: categoryExtendedData.Notes,
            carbonImpactInput: categoryExtendedData.CarbonImpactInput,
            carbonImpactValue: categoryExtendedData.CarbonImpactValue ? parseFloat(categoryExtendedData.CarbonImpactValue) : undefined,
            carbonImpactNotes: categoryExtendedData.CarbonImpactNotes,
            systemId: categoryExtendedData.SystemId,
            subSystemId: categoryExtendedData.SubSystemId,
            attribute1: categoryExtendedData.Attribute1,
            attribute2: categoryExtendedData.Attribute2,
            attribute3: categoryExtendedData.Attribute3,
          };
        }

        return {
          key: c.cadModelCategoryId,
          type: "category",
          data: categoryData,
          children: families,
          leaf: c.childCount === 0,
        } as ModelTreeNode;
      }) || []);

    treeNodes.sort((a: any, b: any) => (a.data.label > b.data.label ? 1 : -1));

    // Restore child components (unchanged)
    if (components.data) {
      visitNodes(treeNodes as ModelTreeNode[], (childNodes: ModelTreeNode[], node: ModelTreeNode) => {
        if (node.type === "element") {
          assignComponentNodes(childNodes, node, components.data as any[], (node as any)?.data?.elementId);
        }
      });
    }

    // Flatten the tree after assigning components
    const rowNodes = flattenizeTree(treeNodes);

    appStore.setAssignNodes(rowNodes as ModelTreeNode[]);

    const validRows = rowNodes.filter((r) => r && r.id && typeof r.depth === "number");
    if (validRows?.length > 0) {
      apiRef.current?.updateRows?.(validRows);
      apiRef.current?.unstable_applyFilters?.();
    }
  };

  useEffect(() => {
    if (appStore.assignNodes === null && !!cadModelCategories.data && !!categories.data && !!elements.data && !!components.data && !!modelExtendedData.data) {

      updateAssignNodes();
    }

  }, [appStore.assignNodesRecalc, cadModelCategories.data, categories.data, elements.data, components.data, modelExtendedData.data]);

  const join = (value: string | undefined) => {
    if (value) {
      return value + "/";
    }
    return "";
  };

  const appendPath = (path: string[], key: string | number | undefined) => {
    if (key) {
      path.push(key.toString());
    }
    return path;
  };

  const flattenizeTree = (
    nodes: ModelTreeNode[] | undefined,
    parentId: string | null = null,
    depth = 0,
    path: string[] = []
  ): any[] => {
    const rows: any[] = [];

    if (!nodes) return rows;

    nodes?.forEach((node, index) => {
     const id = String(node.key ?? `${parentId || "root"}-${index}`);

      const currentPath = [...path, id];

      const row: any = {
        ...node,
        id,        
        depth,       
        parentId,    
        path: currentPath,
      };

      rows.push(row);

      if (node.children && node.children.length > 0) {
        const childRows = flattenizeTree(node.children, id, depth + 1, currentPath);
        rows.push(...childRows);
      }
    });

    return rows;
  };

  const processGroupNodes = (nodes: any, path: string | undefined, minGroupSize: number = -1) => {
    return Object.keys(nodes).reduce((acc: any[], key: string, index: number) => {
      if (minGroupSize !== -1 && nodes[key].length <= minGroupSize) {
        nodes[key].forEach((node: any) => {
          node.key = `${join(path)}${index.toString()}`;
          acc.push(node);
        });
      } else {
        const node = {
          type: "group",
          key: `${join(gPrefix)}${join(path)}${index.toString()}`,
          data: {
            assigned: nodes[key].reduce((acc: boolean, e: any) => {
              return acc && e.data.assigned;
            }, true),
            lengthTotal: nodes[key].reduce((acc: number, e: any) => {
              return acc + (e.data?.lengthTotal || 0);
            }, 0),
            areaTotal: nodes[key].reduce((acc: number, e: any) => {
              return acc + (e.data?.areaTotal || 0);
            }, 0),
            volumeTotal: nodes[key].reduce((acc: number, e: any) => {
              return acc + (e.data?.volumeTotal || 0);
            }, 0),
            totalCo2: nodes[key].reduce((acc: number, e: any) => {
              return acc + (e.data?.totalCo2 || 0);
            }, 0),
            rebarVolumeTotal: nodes[key].reduce((acc: number, e: any) => {
              return acc + (e.data?.rebarVolumeTotal || 0);
            }, 0),
            rebarTotalCo2: nodes[key].reduce((acc: number, e: any) => {
              return acc + (e.data?.rebarTotalCo2 || 0);
            }, 0),
            nonModVolumeTotal: nodes[key].reduce((acc: number, e: any) => {
              return acc + (e.data?.nonModVolumeTotal || 0);
            }, 0),
            nonModTotalCo2: nodes[key].reduce((acc: number, e: any) => {
              return acc + (e.data?.nonModTotalCo2 || 0);
            }, 0),
            label: gPrefix + key.split("/").pop(),
            siblings: nodes[key].length,
          },
          children: nodes[key].sort(
            (a: any, b: any) =>
              a.data?.label?.localeCompare(b.data?.label) ||
              (a.data?.cadId ? a.data.cadId.localeCompare(b.data?.cadId || "") : 1)
          ),
          leaf: nodes[key].length === 0,
        };
        acc.push(node);
      }
      return acc;
    }, []);
  };

  const assignComponentNodes = (
    assignNodes: ModelTreeNode[],
    node: any,
    data: any[],
    parentId: string
  ) => {
    if (assignNodes.length === 0 || !node || !parentId) {
      return;
    }

    const children = data.filter((c: any) => c.parentId === parentId);

    if (children.length > 0) {
      // Create normal component nodes
      let componentNodes = createComponentNodes(children);

      // Collect Rebar supplementals for all components under this element
      let rebarNodes: ModelTreeNode[] = [];
      if (enableRebarLineItems) {
        children.forEach((c: any) => {
          (c.supplementals || [])
            .filter((s: Supplemental) => s.supplementalType === SupplementalType.Rebar && (s.value ?? 0) > 0) // only include rebar supplementals with value > 0
            .forEach((s: Supplemental) => {
              rebarNodes.push(makeRebarNode(c, s));
            });
        });
      }

      // Collect NonModelled supplementals for all components under this element
      let nonModelledNodes: ModelTreeNode[] = [];
      if (enableNonModelledLineItems) {
        children.forEach((c: any) => {
          (c.supplementals || [])
            .filter((s: Supplemental) => s.supplementalType === SupplementalType.NonModelled && (s.value ?? 0) > 0) // only include non-modelled supplementals with value > 0
            .forEach((s: Supplemental) => {
              nonModelledNodes.push(makeNonModelledNode(c, s));
            });
        });
      }

      // Merge: components + rebar siblings (same level under element)
      node.children = [...componentNodes, ...rebarNodes, ...nonModelledNodes];

      node.leaf = node.children.length === 0;
      replaceNode(assignNodes as any[], "key", parentId, node);

      if (node.children) {
        node.data.assigned = node.children.reduce(
          (acc: boolean, e: any) => acc && e.data.assigned,
          true
        );
        node.data.lengthTotal = node.children.reduce(
          (acc: number, e: any) => acc + (e.data?.lengthTotal || 0),
          0
        );
        node.data.areaTotal = node.children.reduce(
          (acc: number, e: any) => acc + (e.data?.areaTotal || 0),
          0
        );
        node.data.volumeTotal = node.children.reduce(
          (acc: number, e: any) => acc + (e.data?.volumeTotal || 0),
          0
        );
        node.data.totalCo2 = node.children.reduce(
          (acc: number, e: any) => acc + (e.data?.totalCo2 || 0),
          0
        );

        node.data.rebarVolumeTotal = node.children.reduce((acc: number, e: any) => {
          const volume = e.data?.supplementals?.find(
            (s: any) => s.supplementalType === SupplementalType.Rebar
          )?.volume;
          return acc + (volume || 0);
        }, 0);

        node.data.rebarTotalCo2 = node.children.reduce((acc: number, e: any) => {
          const material = e.data?.supplementals?.find(
            (s: any) => s.supplementalType === SupplementalType.Rebar
          )?.material;
          return acc + (material?.totalCo2 || 0);
        }, 0);

        node.data.nonModVolumeTotal = node.children.reduce((acc: number, e: any) => {
          const volume = e.data?.supplementals?.find(
            (s: any) => s.supplementalType === SupplementalType.NonModelled
          )?.volume;
          return acc + (volume || 0);
        }, 0);

        node.data.nonModTotalCo2 = node.children.reduce((acc: number, e: any) => {
          const material = e.data?.supplementals?.find(
            (s: any) => s.supplementalType === SupplementalType.NonModelled
          )?.material;
          return acc + (material?.totalCo2 || 0);
        }, 0);
      }
    }
  };

  const createComponentNodes = (nodes: any[]) => {
    const components = nodes.map((c: any) => {
      (c as any).label = coPrefix + (c.cadName || "");
      return {
        key: c.elementId,
        type: "component",
        data: {
          ...c,
          siblings: 1,
        },
        leaf: true,
      };
    });
    return components.sort((a: any, b: any) =>
      a.data?.cadId ? a.data.cadId.localeCompare(b.data?.cadId || "") : 1
    );
  };

  return { loading, hasCo2MaterialTypeSpecs, co2MaterialTypeSpecKind };
};

const replaceNode = (arr: any[], key: string, searchValue: string, updateValue: any) => {
  if (!arr) {
    return;
  }

  for (let i = 0; i < arr.length; i++) {
    if (arr[i][key] === searchValue) {
      arr[i] = updateValue;
    } else if (arr[i] && Array.isArray(arr[i].children)) {
      replaceNode(arr[i].children, key, searchValue, updateValue);
    }
  }
};

// eslint-disable-next-line no-sequences
const groupBy = (x: any, f: any) =>
  x.reduce((a: any, b: any, i: any) => (((a[f(b, i, x)] ||= []).push(b)), a), {});

export default useScenarioModel;

